import { useToast } from 'primevue/usetoast';
import _ from 'lodash';

class ToastManager {
  constructor() {
    this.toastInstance = null;
  }

  // Initialize with Vue component context
  init() {
    if (!this.toastInstance) {
      try {
        this.toastInstance = useToast();
        // eslint-disable-next-line no-unused-vars
      } catch (error) {
        console.error('Toast service must be initialized within a Vue component context');
      }
    }
    return this;
  }

  // Set toast instance manually (for external use)
  setToastInstance(instance) {
    this.toastInstance = instance;
    return this;
  }

  show(severity, message) {
    if (!this.toastInstance) {
      console.error('Toast service not initialized');
      return;
    }

    this.toastInstance.add({
      severity: severity,
      summary: _.capitalize(severity),
      detail: message,
      life: 3000,
      closable: true,
    });
  }

  success(message) {
    this.show('success', message);
  }

  warn(message) {
    this.show('warn', message);
  }

  error(message) {
    this.show('error', message);
  }

  exception(error) {
    if (error instanceof Error) {
      switch (error.code) {
        case 'ECONNABORTED':
          this.error('Connection aborted due to timeout. Please try again later.');
          break;
        case 'ECONNREFUSED':
          this.error('The server refused the connection. Please check if the API is running.');
          break;
        case 'ECONNRESET':
          this.error('The connection was reset. Please retry your request.');
          break;
        case 'ETIMEDOUT':
          this.error('Request timed out before completion. Ensure you have a stable network.');
          break;
        case 'EHOSTUNREACH':
          this.error('The requested host is unreachable. Please verify the server status.');
          break;
        case 'ENOTFOUND':
          this.error('Domain or server not found. Check your API URL or network settings.');
          break;
        default:
          this.error('An unexpected error occurred. Please try again or contact support.');
      }
    } else {
      this.warn('An unexpected error occurred. Please check your connection and try again.');
    }
  }
}

// Create singleton instance
const toastManager = new ToastManager();

// For use inside Vue components
export function useToastService() {
  const toast = useToast();
  toastManager.setToastInstance(toast);

  return {
    success: toastManager.success.bind(toastManager),
    warn: toastManager.warn.bind(toastManager),
    error: toastManager.error.bind(toastManager),
    exception: toastManager.exception.bind(toastManager),
  };
}

// For use outside Vue components
export const ToastService = toastManager;

// Export for global access
export default toastManager;
